import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Upload, Users, ArrowLeft, Check, X } from "lucide-react";

const API_BASE = "https://archpoint.onrender.com/api/clients";

export default function ClientLogoAdmin() {
  const [clients, setClients] = useState([]);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const fetchClients = async () => {
    try {
      const res = await axios.get(API_BASE);
      setClients(res.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setLogo(file);
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  const submitHandler = async () => {
    if (!name.trim() || !logo) {
      alert("Please provide both client name and logo");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("logo", logo);

      await axios.post(API_BASE, formData);
      
      setName("");
      setLogo(null);
      setLogoPreview(null);
      document.getElementById("logo-input").value = "";
      
      setSuccessMessage("Client added successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      
      fetchClients();
    } catch (error) {
      console.error("Failed to add client:", error);
      alert("Failed to add client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    
    setDeleteLoading(id);
    try {
      await axios.delete(`${API_BASE}/${id}`);
      fetchClients();
      setSuccessMessage("Client deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to delete client:", error);
      alert("Failed to delete client. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-amber-200/40 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-yellow-200/40 to-transparent rounded-full blur-3xl"></div>

      {/* Header */}
      <header className="relative border-b border-amber-200/50 bg-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.history.back()}
                className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <Users className="w-8 h-8 text-amber-600" />
                  <h1 className="text-3xl font-bold text-gray-900">
                    Client Logos
                  </h1>
                </div>
                <p className="text-gray-600">
                  Manage client branding assets displayed on your website
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-lg">
              <div className="w-2 h-2 bg-amber-600 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-amber-900">
                {clients.length} Active Clients
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-24 right-6 z-50 animate-slide-in">
          <div className="bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
            <Check className="w-5 h-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Add Client Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl border border-amber-200/50 shadow-xl overflow-hidden">
                {/* Form Header */}
                <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-6">
                  <div className="flex items-center gap-3 text-white">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur">
                      <Plus className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Add New Client</h3>
                      <p className="text-sm text-amber-50">Upload logo and details</p>
                    </div>
                  </div>
                </div>

                {/* Form Body */}
                <div className="p-6 space-y-5">
                  {/* Client Name Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all"
                      placeholder="e.g., Acme Corporation"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Logo
                    </label>
                    <div className="relative">
                      <input
                        id="logo-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <label
                        htmlFor="logo-input"
                        className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-amber-300 rounded-xl cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all group"
                      >
                        {logoPreview ? (
                          <div className="relative w-full h-full p-4">
                            <img
                              src={logoPreview}
                              alt="Preview"
                              className="w-full h-full object-contain"
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setLogo(null);
                                setLogoPreview(null);
                                document.getElementById("logo-input").value = "";
                              }}
                              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-10 h-10 text-amber-400 group-hover:text-amber-600 mb-2 transition" />
                            <span className="text-sm font-medium text-gray-600 group-hover:text-amber-700">
                              Click to upload logo
                            </span>
                            <span className="text-xs text-gray-400 mt-1">
                              PNG, JPG, SVG up to 5MB
                            </span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={submitHandler}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Adding Client...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        <span>Add Client</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Clients Grid */}
          <div className="lg:col-span-2">
            {clients.length === 0 ? (
              <div className="bg-white rounded-2xl border border-amber-200/50 shadow-lg p-12 text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Clients Yet</h3>
                <p className="text-gray-600">Start by adding your first client logo</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {clients.map((client) => (
                  <div
                    key={client._id}
                    className="group relative bg-white rounded-2xl border border-amber-200/50 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    {/* Hover Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-yellow-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Content */}
                    <div className="relative p-6">
                      {/* Logo Container */}
                      <div className="h-32 flex items-center justify-center mb-4 bg-gray-50 rounded-xl group-hover:bg-white transition-colors">
                        <img
                          src={client.logoUrl}
                          alt={client.name}
                          className="max-h-20 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                        />
                      </div>

                      {/* Client Name */}
                      <h4 className="text-center font-bold text-gray-900 mb-4 group-hover:text-amber-700 transition-colors">
                        {client.name}
                      </h4>

                      {/* Delete Button */}
                      <button
                        onClick={() => deleteClient(client._id)}
                        disabled={deleteLoading === client._id}
                        className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {deleteLoading === client._id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Deleting...</span>
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Accent Border */}
                    <div className="absolute inset-0 rounded-2xl border-2 border-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}